import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { equipmentApi, resolveImageUrl, type EquipmentItem } from "@/lib/api";
import ScrollReveal from "@/components/ScrollReveal";



function EquipmentDetailModal({ item, onClose }: { item: EquipmentItem; onClose: () => void }) {
  const specs = [
    { label: "제조사", value: item.manufacturer, icon: "ri-building-line" },
    { label: "모델명", value: item.model, icon: "ri-settings-3-line" },
    { label: "엔진", value: item.engine, icon: "ri-oil-line" },
    { label: "정격 출력", value: item.power_kw, icon: "ri-flashlight-line" },
    { label: "최대 마력", value: item.power_ps, icon: "ri-speed-up-line" },
    { label: "최대 칼날 수", value: item.max_blades, icon: "ri-knife-line" },
    { label: "최대 투입 지름", value: item.max_input_diameter, icon: "ri-ruler-line" },
    { label: "보유 스크린", value: item.screens, icon: "ri-grid-line" },
    { label: "이동 방식", value: item.mobility, icon: "ri-truck-line" },
  ];

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  const modalContent = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-foreground-950/60 z-0"
        onClick={onClose}
      ></div>

      {/* Modal Card */}
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] bg-background-50 rounded-lg overflow-hidden flex flex-col shadow-2xl animate-fade-up">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-background-50/90 backdrop-blur hover:bg-background-50 border border-background-200 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl text-foreground-800 w-5 h-5 flex items-center justify-center"></i>
        </button>

        <div className="relative bg-background-100 flex items-center justify-center max-h-[45vh] min-h-[200px] md:min-h-[320px] overflow-hidden flex-shrink-0 rounded-t-lg">
          <img
            src={resolveImageUrl(item.image)}
            alt={`${item.manufacturer} ${item.model} 칩핑기 사진`}
            className="max-w-full max-h-[45vh] object-contain"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent pointer-events-none"></div>
          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            <span className="px-3 py-1.5 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 text-xs font-semibold tracking-wide">
              {item.model}
            </span>
            <span className="px-3 py-1.5 rounded-full bg-background-50/90 backdrop-blur text-xs font-medium text-foreground-900 tracking-wide">
              {item.manufacturer}
            </span>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 md:p-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            {item.name}
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">
            {item.description}
          </p>

          <div className="rounded-lg border border-background-200/70 overflow-hidden">
            <div className="px-6 py-4 border-b border-background-200/70 bg-background-100/60 flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-primary-500/10 flex items-center justify-center">
                <i className="ri-file-list-3-line text-primary-700 w-4 h-4 flex items-center justify-center"></i>
              </div>
              <span className="font-heading text-base font-semibold text-foreground-950">상세 스펙</span>
            </div>
            <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-background-200/70">
              <div className="divide-y divide-background-200/70">
                {specs.slice(0, 5).map((s) => (
                  <div key={s.label} className="flex items-center justify-between px-6 py-3.5">
                    <span className="text-foreground-600 text-sm flex items-center gap-2">
                      <i className={`${s.icon} text-foreground-400 w-4 h-4 flex items-center justify-center`}></i>
                      {s.label}
                    </span>
                    <span className="text-foreground-950 text-sm font-medium">{s.value}</span>
                  </div>
                ))}
              </div>
              <div className="divide-y divide-background-200/70">
                {specs.slice(5).map((s) => (
                  <div key={s.label} className="flex items-center justify-between px-6 py-3.5">
                    <span className="text-foreground-600 text-sm flex items-center gap-2">
                      <i className={`${s.icon} text-foreground-400 w-4 h-4 flex items-center justify-center`}></i>
                      {s.label}
                    </span>
                    <span className="text-foreground-950 text-sm font-medium">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <a
              href="/inquiries"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap"
            >
              장비 투입 문의하기
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default function Equipment() {
  const [equipmentList, setEquipmentList] = useState<EquipmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentItem | null>(null);

  const fetchEquipment = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await equipmentApi.list();
      setEquipmentList(data || []);
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : "장비 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  return (
    <>
      <section id="equipment" className="relative py-24 md:py-32 bg-background-50 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-100/40 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent-100/40 blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <ScrollReveal direction="up" delay={100}>
            <div className="flex items-center mb-5">
              <div className="inline-flex items-center gap-2 text-primary-700 text-xs tracking-[0.3em] uppercase font-medium">
                <span className="w-8 h-px bg-primary-500"></span>
                보유 장비
              </div>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 leading-tight">
              우수한 성능의{" "}
              <span className="text-accent-600">장비 라인업</span>
            </h2>
            <p className="mt-4 text-foreground-700 text-base md:text-lg max-w-2xl">
              독일 JENZ의 최신 칩핑 기술이 집약된 장비들로, 현장 상황에 맞춰 최적의 장비를 투입합니다.
            </p>
          </ScrollReveal>

          {loading && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 w-8 h-8 flex items-center justify-center"></i>
              <span className="text-foreground-500 text-sm">장비 정보를 불러오는 중...</span>
            </div>
          )}

          {!loading && loadError && (
            <div className="mt-12 py-20 text-center">
              <div className="inline-flex flex-col items-center gap-3">
                <i className="ri-error-warning-line text-3xl w-10 h-10 flex items-center justify-center text-accent-600"></i>
                <span className="text-sm text-foreground-600">{loadError}</span>
                <button
                  onClick={fetchEquipment}
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  다시 시도
                </button>
              </div>
            </div>
          )}

          {!loading && !loadError && equipmentList.length === 0 && (
            <div className="mt-12 py-16 text-center text-foreground-500 text-sm">
              <i className="ri-tools-line w-8 h-8 flex items-center justify-center mx-auto mb-3 text-foreground-300"></i>
              등록된 장비가 없습니다.
            </div>
          )}

          {!loading && !loadError && equipmentList.length > 0 && (
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
              {equipmentList.map((eq, idx) => (
                <ScrollReveal key={eq.id} direction={idx % 2 === 0 ? "left" : "right"} delay={150 + idx * 100}>
                  <div
                    onClick={() => setSelectedEquipment(eq)}
                    className="rounded-lg border border-background-300/80 overflow-hidden bg-background-50 h-full flex flex-col cursor-pointer transition-all hover:border-primary-300 hover:bg-background-50/80 group"
                  >
                    <div className="relative h-[280px] md:h-[340px] overflow-hidden">
                      <img
                        src={resolveImageUrl(eq.image)}
                        alt={`${eq.manufacturer} ${eq.model} 칩핑기 사진`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/40 via-transparent to-transparent pointer-events-none"></div>
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1.5 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 text-xs font-semibold tracking-wide">
                          {eq.model}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <span className="px-3 py-1.5 rounded-full bg-background-50/90 backdrop-blur text-xs font-medium text-foreground-900 tracking-wide flex items-center gap-1.5">
                          <i className="ri-building-line text-primary-700 w-3.5 h-3.5 flex items-center justify-center"></i>
                          {eq.manufacturer}
                        </span>
                      </div>
                      <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="px-3 py-1.5 rounded-full bg-foreground-950/70 backdrop-blur text-xs font-medium text-background-50 tracking-wide flex items-center gap-1.5 pointer-events-none">
                          <i className="ri-zoom-in-line w-3.5 h-3.5 flex items-center justify-center"></i>
                          상세 보기
                        </span>
                      </div>
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3 group-hover:text-primary-700 transition-colors">
                        {eq.name}
                      </h3>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-5 flex-1 line-clamp-3">
                        {eq.description}
                      </p>

                      <div className="grid grid-cols-2 gap-3 mb-5">
                        <div className="flex flex-col gap-1 p-3 rounded-md bg-background-100/70">
                          <span className="text-xs text-foreground-500">엔진</span>
                          <span className="text-sm font-semibold text-foreground-900 leading-tight">{eq.engine || "—"}</span>
                        </div>
                        <div className="flex flex-col gap-1 p-3 rounded-md bg-background-100/70">
                          <span className="text-xs text-foreground-500">출력</span>
                          <span className="text-sm font-semibold text-foreground-900 leading-tight">{eq.power_kw || "—"} / {eq.power_ps || "—"}</span>
                        </div>
                        <div className="flex flex-col gap-1 p-3 rounded-md bg-background-100/70">
                          <span className="text-xs text-foreground-500">최대 투입 지름</span>
                          <span className="text-sm font-semibold text-foreground-900 leading-tight">{eq.max_input_diameter || "—"}</span>
                        </div>
                        <div className="flex flex-col gap-1 p-3 rounded-md bg-background-100/70">
                          <span className="text-xs text-foreground-500">최대 칼날</span>
                          <span className="text-sm font-semibold text-foreground-900 leading-tight">{eq.max_blades || "—"}</span>
                        </div>
                        <div className="flex flex-col gap-1 p-3 rounded-md bg-background-100/70">
                          <span className="text-xs text-foreground-500">보유 스크린</span>
                          <span className="text-sm font-semibold text-foreground-900 leading-tight">{eq.screens || "—"}</span>
                        </div>
                        <div className="flex flex-col gap-1 p-3 rounded-md bg-background-100/70">
                          <span className="text-xs text-foreground-500">이동성</span>
                          <span className="text-sm font-semibold text-foreground-900 leading-tight">{eq.mobility || "—"}</span>
                        </div>
                      </div>

                      <a
                        href="/inquiries"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap w-full"
                      >
                        장비 투입 문의하기
                        <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                      </a>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {selectedEquipment && (
        <EquipmentDetailModal
          item={selectedEquipment}
          onClose={() => setSelectedEquipment(null)}
        />
      )}
    </>
  );
}